import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Spinner, Modal, Button, Form } from "react-bootstrap";
import {
  ChevronRight, ChevronDown, Tag as TagIcon, Layers, School as SchoolIcon,
  Search, Plus, Pencil, Trash2,
} from "lucide-react";
import TagHierarchyService, { TagTreeNode } from "../../_services/tagHierarchy.service";
import PageAccessSettingsButton from "../../access-control/PageAccessSettingsButton";
import { useAccessControl } from "../../access-control/AccessControlContext";
import "./tag-hierarchy.css";

const nodeIcon = (type: TagTreeNode["type"]) => {
  if (type === "tagType") return <Layers size={15} className="th-ic th-ic-type" />;
  if (type === "school") return <SchoolIcon size={14} className="th-ic th-ic-school" />;
  return <TagIcon size={13} className="th-ic th-ic-tag" />;
};

/** Recursively decide if a node or any descendant matches the search text. */
const matches = (node: TagTreeNode, q: string): boolean => {
  if (!q) return true;
  if ((node.title || "").toLowerCase().includes(q)) return true;
  return node.children.some((c) => matches(c, q));
};

interface Perms {
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
}
interface Actions {
  onAddTag: (typeNode: TagTreeNode) => void;
  onAddChild: (tagNode: TagTreeNode) => void;
  onEdit: (tagNode: TagTreeNode) => void;
  onDelete: (tagNode: TagTreeNode) => void;
}

interface TreeItemProps {
  node: TagTreeNode;
  depth: number;
  query: string;
  expandAllSignal: number;
  collapseAllSignal: number;
  perms: Perms;
  actions: Actions;
}

const TreeItem: React.FC<TreeItemProps> = ({ node, depth, query, expandAllSignal, collapseAllSignal, perms, actions }) => {
  const hasChildren = node.children && node.children.length > 0;
  const [open, setOpen] = useState(depth < 2);

  useEffect(() => {
    if (expandAllSignal) setOpen(true);
  }, [expandAllSignal]);
  useEffect(() => {
    if (collapseAllSignal) setOpen(depth < 1);
  }, [collapseAllSignal, depth]);

  const q = query.trim().toLowerCase();
  const visible = matches(node, q);
  const forceOpen = q && hasChildren && node.children.some((c) => matches(c, q));
  const isOpen = open || !!forceOpen;

  if (!visible) return null;

  const isType = node.type === "tagType";
  const isTag = node.type === "tag";

  return (
    <div className="th-node">
      <div
        className={`th-row th-type-${node.type}`}
        style={{ paddingLeft: 8 + depth * 18 }}
        onClick={() => hasChildren && setOpen((o) => !o)}
        role={hasChildren ? "button" : undefined}
      >
        <span className="th-caret">
          {hasChildren ? (
            isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />
          ) : (
            <span className="th-caret-spacer" />
          )}
        </span>
        {nodeIcon(node.type)}
        <span className="th-title" title={node.title}>{node.title}</span>
        {hasChildren && <span className="th-count">{node.children.length}</span>}

        {/* Row actions — stopPropagation so clicking a button doesn't toggle the row */}
        {(isType || isTag) && (
          <span className="th-actions" onClick={(e) => e.stopPropagation()}>
            {perms.canCreate && isType && (
              <button className="th-act th-act-add" title="Add tag" onClick={() => actions.onAddTag(node)}>
                <Plus size={13} /> Add
              </button>
            )}
            {perms.canCreate && isTag && (
              <button className="th-act th-act-add" title="Add child tag" onClick={() => actions.onAddChild(node)}>
                <Plus size={13} />
              </button>
            )}
            {perms.canEdit && isTag && (
              <button className="th-act th-act-edit" title="Edit tag" onClick={() => actions.onEdit(node)}>
                <Pencil size={12} />
              </button>
            )}
            {perms.canDelete && isTag && (
              <button className="th-act th-act-del" title="Delete tag" onClick={() => actions.onDelete(node)}>
                <Trash2 size={12} />
              </button>
            )}
          </span>
        )}
      </div>
      {hasChildren && isOpen && (
        <div className="th-children">
          {node.children.map((c) => (
            <TreeItem
              key={`${c.type}-${c.key}`}
              node={c}
              depth={depth + 1}
              query={query}
              expandAllSignal={expandAllSignal}
              collapseAllSignal={collapseAllSignal}
              perms={perms}
              actions={actions}
            />
          ))}
        </div>
      )}
    </div>
  );
};

type FormMode =
  | { kind: "addUnderType"; typeId: number; typeName: string }
  | { kind: "addChild"; parentTagId: number; parentTitle: string }
  | { kind: "edit"; tagId: number; title: string };

interface TagHierarchyProps {
  appliesTo: string;
  pageKey: string;
  heading: string;
}

const TagHierarchy: React.FC<TagHierarchyProps> = ({ appliesTo, pageKey, heading }) => {
  const { canSeeElement } = useAccessControl();
  const perms: Perms = {
    canCreate: canSeeElement(pageKey, "action.create"),
    canEdit: canSeeElement(pageKey, "action.edit"),
    canDelete: canSeeElement(pageKey, "action.delete"),
  };

  const [tree, setTree] = useState<TagTreeNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [expandAllSignal, setExpandAllSignal] = useState(0);
  const [collapseAllSignal, setCollapseAllSignal] = useState(0);
  const [banner, setBanner] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Create/Edit modal
  const [formMode, setFormMode] = useState<FormMode | null>(null);
  const [formName, setFormName] = useState("");
  const [formCode, setFormCode] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [saving, setSaving] = useState(false);

  // Delete modal
  const [deleteTarget, setDeleteTarget] = useState<TagTreeNode | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await TagHierarchyService.get(appliesTo);
      if (res.data?.success) {
        setTree(res.data.data?.tree || []);
      } else {
        setTree([]);
        setError(res.data?.message || "Failed to load tag hierarchy");
      }
    } catch (err: any) {
      setTree([]);
      const status = err?.response?.status;
      setError(
        status === 401
          ? "Your session has expired. Please log in again."
          : `Failed to load tag hierarchy${status ? ` (${status})` : ""}: ${err?.response?.data?.message || err?.message || "network error"}`,
      );
    } finally {
      setLoading(false);
    }
  }, [appliesTo]);

  useEffect(() => {
    load();
  }, [load]);

  const flashBanner = (type: "success" | "error", text: string) => {
    setBanner({ type, text });
    window.setTimeout(() => setBanner(null), 4000);
  };

  // ---- action openers ----
  const openAddUnderType = (typeNode: TagTreeNode) => {
    setFormMode({ kind: "addUnderType", typeId: typeNode.key, typeName: typeNode.title });
    setFormName(""); setFormCode(""); setFormDesc("");
  };
  const openAddChild = (tagNode: TagTreeNode) => {
    setFormMode({ kind: "addChild", parentTagId: tagNode.key, parentTitle: tagNode.title });
    setFormName(""); setFormCode(""); setFormDesc("");
  };
  const openEdit = (tagNode: TagTreeNode) => {
    setFormMode({ kind: "edit", tagId: tagNode.key, title: tagNode.title });
    setFormName(tagNode.name ?? ""); setFormCode(tagNode.code ?? ""); setFormDesc(tagNode.description ?? "");
  };

  const actions: Actions = {
    onAddTag: openAddUnderType,
    onAddChild: openAddChild,
    onEdit: openEdit,
    onDelete: (n) => setDeleteTarget(n),
  };

  const closeForm = () => { setFormMode(null); setSaving(false); };

  const handleSave = async () => {
    if (!formMode) return;
    if (!formName.trim()) return;
    setSaving(true);
    try {
      if (formMode.kind === "edit") {
        await TagHierarchyService.update(formMode.tagId, {
          name: formName.trim(), code: formCode.trim(), description: formDesc,
        });
        flashBanner("success", "Tag updated");
      } else {
        await TagHierarchyService.create({
          name: formName.trim(),
          code: formCode.trim(),
          description: formDesc,
          ...(formMode.kind === "addUnderType"
            ? { tagTypeId: formMode.typeId }
            : { parentTagId: formMode.parentTagId }),
        });
        flashBanner("success", "Tag created");
      }
      closeForm();
      await load();
    } catch (err: any) {
      flashBanner("error", err?.response?.data?.message || err?.message || "Save failed");
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await TagHierarchyService.remove(deleteTarget.key);
      if (res.data?.success) {
        flashBanner("success", res.data.message || "Deleted");
        setDeleteTarget(null);
        await load();
      } else {
        flashBanner("error", res.data?.message || "Delete failed");
      }
    } catch (err: any) {
      // Java guards surface here: "Tag has children…" / "Tag mapped to questions…"
      flashBanner("error", err?.response?.data?.message || err?.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const q = search.trim().toLowerCase();
  const visibleTree = useMemo(() => tree.filter((n) => matches(n, q)), [tree, q]);
  const typeCount = tree.length;

  const formTitle =
    formMode?.kind === "edit" ? "Edit Tag"
    : formMode?.kind === "addChild" ? `Add tag under “${formMode.parentTitle}”`
    : formMode?.kind === "addUnderType" ? `Add tag in “${formMode.typeName}”`
    : "";

  return (
    <div className="th-root">
      <div className="th-header">
        <div>
          <h4 className="th-page-title">{heading}</h4>
        </div>
        <PageAccessSettingsButton pageKey={pageKey} />
      </div>

      {banner && <div className={`th-banner th-banner-${banner.type}`}>{banner.text}</div>}

      <div className="th-toolbar">
        <div className="th-search">
          <Search size={15} className="th-search-ic" />
          <input
            className="th-search-input"
            placeholder="Filter tags…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="th-toolbar-right">
          {!loading && <span className="th-meta">{typeCount} tag {typeCount === 1 ? "type" : "types"}</span>}
          <button className="th-btn" onClick={() => setExpandAllSignal((n) => n + 1)}>Expand all</button>
          <button className="th-btn" onClick={() => setCollapseAllSignal((n) => n + 1)}>Collapse all</button>
        </div>
      </div>

      <div className="th-tree-card">
        {loading ? (
          <div className="th-state"><Spinner animation="border" size="sm" /> Loading…</div>
        ) : error ? (
          <div className="th-state th-error">{error}</div>
        ) : visibleTree.length === 0 ? (
          <div className="th-state th-empty">
            {tree.length === 0 ? "No tag types configured for this scope." : "No tags match your filter."}
          </div>
        ) : (
          visibleTree.map((n) => (
            <TreeItem
              key={`${n.type}-${n.key}`}
              node={n}
              depth={0}
              query={search}
              expandAllSignal={expandAllSignal}
              collapseAllSignal={collapseAllSignal}
              perms={perms}
              actions={actions}
            />
          ))
        )}
      </div>

      {/* Create / Edit modal */}
      <Modal show={!!formMode} onHide={closeForm} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1.05rem" }}>{formTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 600, fontSize: "0.85rem" }}>Name <span style={{ color: "var(--status-danger)" }}>*</span></Form.Label>
            <Form.Control size="sm" value={formName} autoFocus
              onChange={(e) => setFormName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 600, fontSize: "0.85rem" }}>Code</Form.Label>
            <Form.Control size="sm" value={formCode} onChange={(e) => setFormCode(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label style={{ fontWeight: 600, fontSize: "0.85rem" }}>Description</Form.Label>
            <Form.Control as="textarea" rows={2} size="sm" value={formDesc} onChange={(e) => setFormDesc(e.target.value)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={closeForm} disabled={saving}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || !formName.trim()}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete confirm modal */}
      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1.05rem" }}>Delete Tag</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete <strong>{deleteTarget?.title}</strong>?</p>
          <p style={{ color: "var(--text-muted)", fontSize: 13 }}>
            This will fail if the tag has child tags or is mapped to questions.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button variant="danger" size="sm" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

// Scope-specific pages. Each is wired to its own route + pageKey in the router
// registry so tenant access can be granted per scope.
export const QuestionTagHierarchy: React.FC = () => (
  <TagHierarchy appliesTo="question" pageKey="tag-hierarchy-question" heading="Question Tag Hierarchy" />
);
export const SchoolTagHierarchy: React.FC = () => (
  <TagHierarchy appliesTo="school" pageKey="tag-hierarchy-school" heading="School Tag Hierarchy" />
);
export const TopicTagHierarchy: React.FC = () => (
  <TagHierarchy appliesTo="topic" pageKey="tag-hierarchy-topic" heading="Topic Tag Hierarchy" />
);
export const BatchTagHierarchy: React.FC = () => (
  <TagHierarchy appliesTo="batch" pageKey="tag-hierarchy-batch" heading="Batch Tag Hierarchy" />
);

export default TagHierarchy;
